/** @jsx jsx */
import { Button } from '@datacamp/waffles/button';
import { Moon, Sun } from '@datacamp/waffles/icon';
import { Tooltip } from '@datacamp/waffles/tooltip';
import { jsx } from '@emotion/react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  darkThemePanelButton,
  lightThemePanelButton,
  panelButtonPosition,
} from '../styles/campus/buttonOverrides';

type Props = {
  toggleUITheme: (...args: any[]) => any;
  uiTheme: UiTheme;
};

export type UiTheme = 'DARK' | 'LIGHT';

const ThemeToggler: React.FC<Props> = ({ toggleUITheme, uiTheme }) => {
  const buttonRef = React.useRef(null);
  const { t } = useTranslation();
  const tooltipText = t('ThemeToggler.tooltip');

  const blurAndSwitch = useCallback(() => {
    if (buttonRef.current != null) {
      // @ts-expect-error object possibly null
      buttonRef.current.blur();
    }
    toggleUITheme();
  }, [buttonRef, toggleUITheme]);

  if (uiTheme === 'LIGHT') {
    return (
      <Tooltip
        content={tooltipText}
        placement="bottomRight"
        css={{ maxWidth: 320 }}
      >
        <Button
          variant="plain"
          onClick={blurAndSwitch}
          aria-label={t('ThemeToggler.toggleThemeLabel')}
          ref={buttonRef}
          data-cy="theme-mode-changed-to-dark"
          iconLeft={<Moon size="xsmall" />}
          css={[panelButtonPosition, lightThemePanelButton]}
        >
          {t('ThemeToggler.darkMode')}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      content={tooltipText}
      placement="bottomRight"
      inverted
      css={{ maxWidth: 320 }}
    >
      <Button
        variant="plain"
        onClick={blurAndSwitch}
        aria-label={t('ThemeToggler.toggleThemeLabel')}
        ref={buttonRef}
        data-cy="theme-mode-changed-to-light"
        iconLeft={<Sun size="xsmall" />}
        css={[panelButtonPosition, darkThemePanelButton]}
        inverted
      >
        {t('ThemeToggler.lightMode')}
      </Button>
    </Tooltip>
  );
};

export default ThemeToggler;
